import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState(null); // pilot mode only

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
      // Pilot mode: backend may return resetLink
      if (response.data.resetLink) {
        setResetLink(response.data.resetLink);
      }
    } catch (error) {
      if (error?.response?.status === 429) {
        toast.error(t('auth.tooManyRequests') || 'Too many requests. Please try again later.');
      } else {
        // Still show success to prevent enumeration
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-gray-900">{t('auth.resetPassword') || 'Reset Password'}</h1>
          <p className="text-gray-600 mt-2">
            {submitted
              ? (t('auth.resetEmailSent') || 'If an account with that email exists, a reset link has been sent.')
              : (t('auth.resetInstructions') || 'Enter your email and we\'ll send you a link to reset your password.')}
          </p>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
                className="mt-1"
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || !email.trim()}>
              <Mail className="w-4 h-4 mr-2" />
              {loading ? (t('common.loading') || 'Sending...') : (t('auth.sendResetLink') || 'Send Reset Link')}
            </Button>
          </form>
        ) : (
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-center text-sm text-gray-600">
              {t('auth.checkEmail') || 'Check your email for a password reset link.'}
            </p>

            {/* Pilot mode: show direct reset link */}
            {resetLink && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-medium text-blue-800">{t('auth.pilotMode') || 'Pilot Mode — Direct Reset Link:'}</p>
                <a
                  href={resetLink}
                  className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 font-medium break-all"
                >
                  <ExternalLink className="w-4 h-4 flex-shrink-0" />
                  {t('auth.openResetLink') || 'Open Reset Link'}
                </a>
              </div>
            )}

            <Button
              variant="outline"
              className="w-full"
              onClick={() => { setSubmitted(false); setResetLink(null); setEmail(''); }}
            >
              {t('auth.tryAnotherEmail') || 'Try another email'}
            </Button>
          </div>
        )}

        <div className="text-center">
          <Link to="/login" className="text-sm text-primary hover:text-primary-hover inline-flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('auth.backToLogin') || 'Back to Login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
